<template>
  <div>
    <div class="flex flex-col lg:flex-row lg:items-center gap-10">
      <TImage
        :src="slide0Image"
        alt=""
        width="450"
        height="290"
        class="mx-auto"
      />

      <div>
        <THeading
          :level="2"
          tag="h2"
          class="mb-3"
        >
          {{ title }}
        </THeading>

        <TBody variant="muted">
          {{ t('description') }}
        </TBody>
      </div>
    </div>

    <div class="grid lg:grid-cols-3 gap-6 my-12">
      <div>
        <THeading
          :level="5"
          tag="h3"
          class="mb-2"
        >
          {{ t('pickRV.title') }}
        </THeading>

        <TBody variant="muted">
          {{ t('pickRV.description') }}
        </TBody>
      </div>

      <div>
        <THeading
          :level="5"
          tag="h3"
          class="mb-2"
        >
          {{ t('findCampground.title') }}
        </THeading>

        <TBody variant="muted">
          {{ t('findCampground.description') }}
        </TBody>
      </div>

      <div>
        <THeading
          :level="5"
          tag="h3"
          class="mb-2"
        >
          {{ t('travelWithConfidence.title') }}
        </THeading>

        <TBody variant="muted">
          {{ t('travelWithConfidence.description') }}
        </TBody>
      </div>
    </div>

    <div
      v-if="campgrounds?.length"
      class="space-y-6"
    >
      <THeading
        :level="5"
        tag="h3"
      >
        {{ t('pickRVFromCampground') }}
      </THeading>

      <div class="flex flex-wrap gap-4">
        <NuxtLink
          v-for="campground in campgrounds"
          :key="campground.id"
          :to="localePath({ name: ROUTE_BASE_NAME.rv_rentals_campground, params: { campgroundSlug: campground.id } })"
          class="font-medium border border-gray-200 rounded-lg py-2 px-3 hover:underline"
        >
          {{ campground.name }}
        </NuxtLink>
      </div>

      <NuxtLink
        :to="searchCampgroundsUrl"
        class="text-gray-500 underline hover:no-underline"
      >
        {{ t('searchCampgrounds') }}
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ROUTE_BASE_NAME } from '~/constants'

const props = defineProps<{
  campgrounds?: Array<{
    name: string
    id: string
  }>
  keepCurrentSeoContent?: boolean
  locationNearby?: string
  campgroundCount?: number
}>()

const { t } = useI18n()
const localePath = useLocalePath()
const searchData = useSearchData()

const title = computed(() => {
  if (props.keepCurrentSeoContent !== false) {
    return t('title')
  }

  return t('titleDestination', { locationNearby: props.locationNearby, n: props.campgroundCount ?? props.campgrounds?.length ?? 0 })
})

const searchCampgroundsUrl = computed(() => {
  return localePath({ name: ROUTE_BASE_NAME.campgrounds_search, query: { PlaceId: searchData.value.location?.placeId } })
})

const { getStaticImage } = useImage()
const slide0Image = computed(() => getStaticImage({
  path: '/web/images/custom-icons/discover-delivery/slide0.svg',
}))
</script>

<i18n lang="json">
  {
    "en": {
      "title": "RV delivery to a campground, made simple",
      "titleDestination": "RV delivery to campgrounds {locationNearby} | RV delivery to a campground {locationNearby} | RV delivery to {n} campgrounds {locationNearby}",
      "description": "Skip the pickup. Skip the towing. Get an RV delivered and set up at your campsite so you can arrive, unpack, and start your trip stress-free.",
      "pickRV": {
        "title": "Choose your perfect RV",
        "description": "Browse RVs that offer delivery to your site and book the one that fits your budget and adventure style."
      },
      "findCampground": {
        "title": "Lock in your campsite",
        "description": "Reserve your campsite with the hookups and amenities you need. We’ll help you find RVs that meet site requirements."
      },
      "travelWithConfidence": {
        "title": "Arrive and relax",
        "description": "Your host delivers and sets up the RV before you get there. Just show up and start enjoying your trip."
      },
      "pickRVFromCampground": "Find the perfect RV for these nearby campgrounds",
      "searchCampgrounds": "Search campgrounds"
    },
    "fr": {
      "title": "Livraison de VR au camping, simplifiée",
      "titleDestination": "Livraison de VR aux campings {locationNearby} | Livraison de VR à un camping {locationNearby} | Livraison de VR à {n} campings {locationNearby}",
      "description": "Évitez le ramassage et le remorquage. Obtenez une livraison de VR et une installation à votre site de camping pour que vous puissiez arriver, défaire vos bagages et commencer votre voyage sans stress.",
      "pickRV": {
        "title": "Choisissez votre VR idéal",
        "description": "Parcourez les VRs qui offrent la livraison à votre site et réservez celui qui correspond à votre budget et à votre style d'aventure."
      },
      "findCampground": {
        "title": "Réservez votre site de camping",
        "description": "Réservez votre site de camping avec les équipements et les commodités dont vous avez besoin. Nous vous aiderons à trouver des VRs qui répondent aux exigences du site."
      },
      "travelWithConfidence": {
        "title": "Arrivez et relaxez",
        "description": "Votre hôte livre et installe la VR avant votre arrivée. Il vous suffit de vous présenter et de commencer à profiter de votre voyage."
      },
      "pickRVFromCampground": "Trouvez le VR idéal pour ces sites de camping près de chez vous",
      "searchCampgrounds": "Rechercher les campings"
    }
  }
</i18n>
